import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Play, ExternalLink } from "lucide-react";
import {
  GALLERY_VIDEOS,
  getYouTubeThumbnail,
  getYouTubeEmbedUrl,
} from "@/lib/videos";

/**
 * Impact-focused YouTube video gallery.
 * - Bento grid: first video large, next 5 as smaller tiles
 * - Click-to-play: lazy loads iframes only on user interaction
 * - Each tile carries VideoObject JSON-LD (SEO)
 */
export const VideoGallery = () => {
  const [playingId, setPlayingId] = useState(null);

  // Inject VideoObject JSON-LD for each video so Google / AI search can index
  useEffect(() => {
    const id = "gallery-videos-jsonld";
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    const seen = new Set();
    const items = GALLERY_VIDEOS.filter((v) => {
      // Dedupe repeated IDs for the JSON-LD feed (Google doesn't like dupes)
      if (seen.has(v.id)) return false;
      seen.add(v.id);
      return true;
    }).map((v) => ({
      "@type": "VideoObject",
      name: v.title,
      description: v.description,
      thumbnailUrl: getYouTubeThumbnail(v.id, "maxres"),
      uploadDate: v.uploadDate,
      duration: v.duration,
      contentUrl: `https://www.youtube.com/watch?v=${v.id}`,
      embedUrl: `https://www.youtube.com/embed/${v.id}`,
      publisher: {
        "@type": "Organization",
        name: "drones3x",
        logo: {
          "@type": "ImageObject",
          url: "https://drones3x.fr/logo.png",
        },
      },
    }));

    el.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": items,
    });

    return () => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
    };
  }, []);

  const first = GALLERY_VIDEOS[0];
  const rest = GALLERY_VIDEOS.slice(1);

  const Tile = ({ v, large = false, index }) => {
    const isPlaying = playingId === `${v.id}-${index}`;
    return (
      <motion.div
        data-testid={`gallery-video-${index}`}
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-10%" }}
        transition={{ duration: 0.6, delay: large ? 0 : index * 0.06 }}
        className={`relative group overflow-hidden rounded-3xl glass-strong ${
          large ? "md:col-span-2 md:row-span-2" : ""
        }`}
      >
        {isPlaying ? (
          <iframe
            src={getYouTubeEmbedUrl(v.id, { autoplay: true })}
            title={v.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="absolute inset-0 h-full w-full z-10"
          />
        ) : (
          <button
            onClick={() => setPlayingId(`${v.id}-${index}`)}
            className="relative block w-full h-full text-left"
            aria-label={`Lire la vidéo : ${v.title}`}
          >
            <div className={large ? "aspect-video md:aspect-[4/3]" : "aspect-video"}>
              <img
                src={getYouTubeThumbnail(v.id, "maxres")}
                alt={v.title}
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.src = getYouTubeThumbnail(v.id, "hq");
                }}
                className="absolute inset-0 h-full w-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#030305]/90 via-[#030305]/30 to-transparent" />
            </div>

            {/* Category badge */}
            <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full glass-strong px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold text-white">
              {v.category}
            </span>

            {/* Play button */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={`relative inline-flex items-center justify-center rounded-full bg-[#007AFF] text-white shadow-[0_0_40px_rgba(0,122,255,0.5)] group-hover:scale-110 transition ${
                large ? "h-20 w-20 md:h-24 md:w-24" : "h-14 w-14"
              }`}>
                <span className="absolute inset-0 rounded-full bg-[#007AFF] animate-ping opacity-30" />
                <Play size={large ? 32 : 20} className="relative ml-1" fill="currentColor" />
              </span>
            </div>

            {/* Title overlay */}
            <div className="absolute bottom-0 inset-x-0 p-5 md:p-6">
              <h3 className={`font-display font-semibold leading-tight tracking-tight ${
                large ? "text-xl md:text-2xl lg:text-3xl" : "text-base md:text-lg"
              }`}>
                {v.title}
              </h3>
              {large && (
                <p className="hidden md:block mt-2 text-sm text-white/70 max-w-xl line-clamp-2">
                  {v.description}
                </p>
              )}
            </div>
          </button>
        )}
      </motion.div>
    );
  };

  return (
    <section
      id="gallery"
      data-testid="video-gallery-root"
      className="relative py-28 md:py-32"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(0,122,255,0.08),transparent_60%)] pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-xs uppercase tracking-[0.2em] text-white/70">
              Galerie vidéo
            </div>
            <h2 className="font-display mt-6 text-4xl md:text-5xl lg:text-6xl font-light tracking-tight">
              Nos <span className="italic font-semibold text-[#007AFF]">spectacles de drones</span><br />
              en plein vol.
            </h2>
          </div>
          <p className="text-white/60 max-w-md leading-relaxed">
            Mariages, lancements de marque, festivals, cérémonies sportives — découvrez
            quelques-uns de nos spectacles de drones les plus marquants.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 md:auto-rows-[220px]">
          <Tile v={first} large index={0} />
          {rest.map((v, i) => (
            <Tile key={`${v.id}-${i + 1}`} v={v} index={i + 1} />
          ))}
        </div>

        <div className="mt-14 flex flex-wrap items-center justify-center gap-4 text-sm">
          <a
            href="https://www.youtube.com/results?search_query=drones3x+spectacle+drones"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full glass px-6 py-3 text-white hover:bg-white/10 transition"
          >
            <ExternalLink size={14} /> Voir toutes nos vidéos sur YouTube
          </a>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 rounded-full bg-[#007AFF] px-6 py-3 font-semibold hover:shadow-[0_0_32px_rgba(0,122,255,0.55)] transition"
          >
            Commander votre spectacle →
          </a>
        </div>
      </div>
    </section>
  );
};

export default VideoGallery;
